import { useCallback, useEffect, useState } from 'react';

import { COLOR_SCHEME_PREFERENCE_KEY } from '@/constants';
import { useMediaQuery } from './use-media-query';

/** What the reader chose, persisted in localStorage. `system` follows the OS. */
export type ColorSchemePreference = 'system' | 'light' | 'dark';

/** What actually gets applied to `<html data-theme>` -- `system` is resolved away before this point. */
export type ColorScheme = 'light' | 'dark';

function readStoredPreference(): ColorSchemePreference {
	const stored = localStorage.getItem(COLOR_SCHEME_PREFERENCE_KEY);
	if (stored === 'system' || stored === 'light' || stored === 'dark') {
		return stored;
	}
	// No preference saved yet (or a corrupted value) defaults to light, not
	// system, for now -- this is a provisional choice (matches
	// index-template.ejs's own pre-paint default) and may change once
	// Section 20's tenant-accent dark-mode contrast audit
	// (improvements/DASHBOARD.md) has happened.
	return 'light';
}

export interface UseColorSchemeResult {
	/** The resolved value -- what the compiled theme CSS actually keys off. */
	colorScheme: ColorScheme;
	/** The reader's raw choice, e.g. for a control to show which option is selected. */
	preference: ColorSchemePreference;
	/** Persists the choice and applies it immediately. The eventual reader-settings control calls this. */
	setColorSchemePreference: (preference: ColorSchemePreference) => void;
}

/**
 * The app's current color scheme, kept in sync with `<html data-theme>` --
 * the attribute the compiled theme CSS keys its dark values off
 * (`:root[data-theme='dark']`, see scripts/lib/theme/compile.ts and
 * docs/adr/0007-dark-mode-selection.md).
 *
 * The *initial* value is resolved before first paint by the inline script in
 * index-template.ejs (same localStorage key, same resolution rule), so
 * there is no flash-of-wrong-theme -- this hook does not own that moment.
 * What it does own is keeping the attribute (and the React value read off
 * AppContext) live for the rest of the session: a `system` preference
 * reacting to an OS change while the app stays open, and any change made
 * through `setColorSchemePreference` (there is no reader-facing control
 * calling it yet -- this is the seam Section 20's toggle wires into).
 */
export function useColorScheme(): UseColorSchemeResult {
	const [preference, setPreference] = useState<ColorSchemePreference>(
		readStoredPreference,
	);
	const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

	const colorScheme: ColorScheme =
		preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference;

	useEffect(() => {
		document.documentElement.setAttribute('data-theme', colorScheme);
	}, [colorScheme]);

	const setColorSchemePreference = useCallback(
		(next: ColorSchemePreference) => {
			localStorage.setItem(COLOR_SCHEME_PREFERENCE_KEY, next);
			setPreference(next);
		},
		[],
	);

	return { colorScheme, preference, setColorSchemePreference };
}
